import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, Mail, Phone, Linkedin, Edit2, Trash2, Plus, CheckCircle2, Ticket, TrendingUp } from 'lucide-react';
import { crmContactsApi, crmConversationsApi } from '../../api/crm';
import ConversationList from '../../components/crm/ConversationList';
import ComposeEmailModal from '../../components/crm/ComposeEmailModal';
import { useCompany } from '../../contexts/CompanyContext';
import { useConfirm } from '../../components/ConfirmDialog';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';
import ContactForm from '../../components/crm/ContactForm';
import CrmActivityItem from '../../components/crm/CrmActivityItem';
import CrmActivityForm from '../../components/crm/CrmActivityForm';
import type { CrmContactStatus } from '../../types';

// Domain status taxonomy → token-colored text label.
const STATUS_COLOR: Record<CrmContactStatus, string> = {
  lead:     'var(--scpa-text-muted)',
  prospect: 'var(--scpa-primary)',
  customer: 'var(--scpa-status-ok)',
  churned:  'var(--scpa-status-error)',
  archived: 'var(--scpa-text-muted)',
};

type Tab = 'overview' | 'conversations' | 'tickets' | 'deals' | 'activities';

export default function ContactDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { currentCompany } = useCompany();
  const cid = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;
  const [tab, setTab] = useState<Tab>('overview');
  const [editing, setEditing] = useState(false);
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [composing, setComposing] = useState(false);

  const { data: conversations } = useQuery({
    queryKey: ['crm-conversations', 'contact', id, cid],
    queryFn: () => crmConversationsApi.list({ contactId: id!, status: 'all', limit: 100 }),
    enabled: !!id && !!cid,
  });

  const { data: contact, isLoading } = useQuery({
    queryKey: ['crm-contact', id, cid],
    queryFn: () => crmContactsApi.get(id!),
    enabled: !!id && !!cid,
  });

  const deleteMutation = useMutation({
    mutationFn: () => crmContactsApi.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-contacts'] });
      navigate('/crm/contacts');
    },
  });

  const handleDelete = async () => {
    if (await confirm({ title: 'Delete contact', message: 'Delete this contact? This cannot be undone.', confirmLabel: 'Delete', danger: true })) {
      deleteMutation.mutate();
    }
  };

  if (isLoading) return <div className="text-muted text-sm">Loading…</div>;
  if (!contact) return <div className="text-muted text-sm">Contact not found.</div>;

  return (
    <div className="flex flex-col gap-4 pb-10">
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <span className="w-12 h-12 rounded-full bg-primary-muted flex items-center justify-center text-primary text-lg font-bold">
              {contact.firstName[0]}{contact.lastName?.[0] ?? ''}
            </span>
            <span className="flex flex-col">
              <span>{contact.firstName} {contact.lastName}</span>
              {contact.jobTitle && (
                <span className="text-[13px] font-normal text-muted">
                  {contact.jobTitle}{contact.organization ? ' · ' : ''}
                  {contact.organization && <Link to={`/crm/organizations/${contact.organization.id}`} className="text-primary hover:underline">{contact.organization.name}</Link>}
                </span>
              )}
            </span>
          </span>
        }
        actions={
          <>
            <span className="mr-1" style={{ fontSize: 12, fontWeight: 700, color: STATUS_COLOR[contact.status], textTransform: 'capitalize' }}>{contact.status}</span>
            {contact.email && (
              <button onClick={() => setComposing(true)} className="btn btn-secondary flex items-center gap-1" title="Email this contact from a CRM mailbox — their reply lands in the Inbox">
                <Mail className="w-3.5 h-3.5" /> Email
              </button>
            )}
            <button onClick={() => setEditing(true)} className="btn btn-secondary flex items-center gap-1">
              <Edit2 className="w-3.5 h-3.5" /> Edit
            </button>
            <button onClick={handleDelete} className="btn btn-danger flex items-center gap-1">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </>
        }
      />

      {/* Tabs */}
      <div className="border-b border-line">
        <nav className="flex gap-4">
          {(['overview', 'conversations', 'tickets', 'deals', 'activities'] as Tab[]).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors capitalize ${tab === t ? 'border-primary text-primary' : 'border-transparent text-muted hover:text-fg'}`}
            >
              {t}
              {t === 'conversations' && conversations?.total ? <span className="ml-1.5 text-xs text-muted">({conversations.total})</span> : null}
              {t === 'tickets' && contact.tickets?.length ? <span className="ml-1.5 text-xs text-muted">({contact.tickets.length})</span> : null}
              {t === 'deals' && contact.deals?.length ? <span className="ml-1.5 text-xs text-muted">({contact.deals.length})</span> : null}
              {t === 'activities' && contact.activities?.length ? <span className="ml-1.5 text-xs text-muted">({contact.activities.length})</span> : null}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {tab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            {contact.notes && (
              <div className="card">
                <h3 className="section-title mb-2">Notes</h3>
                <p className="text-sm text-fg whitespace-pre-wrap">{contact.notes}</p>
              </div>
            )}
            {contact.tags?.length > 0 && (
              <div className="card">
                <h3 className="section-title mb-2">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {contact.tags.map(tag => <span key={tag} className="badge bg-primary-muted text-primary">{tag}</span>)}
                </div>
              </div>
            )}
          </div>
          <div className="space-y-4">
            <div className="card space-y-3">
              <h3 className="section-title">Contact Info</h3>
              {contact.email && <a href={`mailto:${contact.email}`} className="flex items-center gap-2 text-sm text-fg hover:text-primary"><Mail className="w-4 h-4 text-muted" />{contact.email}</a>}
              {contact.phone && <span className="flex items-center gap-2 text-sm text-fg"><Phone className="w-4 h-4 text-muted" />{contact.phone}</span>}
              {contact.mobile && <span className="flex items-center gap-2 text-sm text-fg"><Phone className="w-4 h-4 text-muted" />{contact.mobile} (mobile)</span>}
              {contact.linkedInUrl && <a href={contact.linkedInUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-primary hover:underline"><Linkedin className="w-4 h-4" />LinkedIn</a>}
              {contact.organization && <Link to={`/crm/organizations/${contact.organization.id}`} className="flex items-center gap-2 text-sm text-fg hover:text-primary"><Building2 className="w-4 h-4 text-muted" />{contact.organization.name}</Link>}
              {contact.source && <p className="text-xs text-muted">Source: {contact.source}</p>}
            </div>
            {contact.portalUser && (
              <div className="card">
                <h3 className="section-title mb-2 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-ok" /> Portal Access</h3>
                <p className="text-sm text-fg">{contact.portalUser.name}</p>
                <p className="text-xs text-muted">{contact.portalUser.email}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'conversations' && (
        <ConversationList
          conversations={conversations?.conversations ?? []}
          compact
          empty={<>No email conversations with this contact yet. Use <b>Email</b> above to start one.</>}
        />
      )}

      {tab === 'tickets' && (
        <div className="space-y-2">
          {!contact.tickets?.length ? (
            <EmptyState icon={<Ticket className="w-8 h-8" />}>No tickets linked to this contact.</EmptyState>
          ) : contact.tickets.map(t => (
            <Link key={t.id} to={`/tickets/${t.id}`} className="card flex items-center justify-between hover:bg-row-odd transition-colors">
              <div>
                <span className="text-xs text-muted mr-2">#{t.ticketNumber}</span>
                <span className="font-medium text-sm text-fg">{t.title}</span>
              </div>
              <span className="text-xs font-semibold text-muted capitalize">{t.status.replace('_', ' ')}</span>
            </Link>
          ))}
        </div>
      )}

      {tab === 'deals' && (
        <div className="space-y-2">
          {!contact.deals?.length ? (
            <EmptyState icon={<TrendingUp className="w-8 h-8" />}>No deals linked to this contact.</EmptyState>
          ) : contact.deals.map(d => (
            <Link key={d.id} to={`/crm/deals/${d.id}`} className="card flex items-center justify-between hover:bg-row-odd transition-colors">
              <div>
                <p className="font-medium text-sm text-fg">{d.title}</p>
                <p className="text-xs text-muted">{d.stage?.name}</p>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-fg">{d.value ? `$${d.value.toLocaleString()}` : '—'}</p>
                <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'capitalize', color: d.status === 'won' ? 'var(--scpa-status-ok)' : d.status === 'lost' ? 'var(--scpa-status-error)' : 'var(--scpa-text-muted)' }}>{d.status}</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      {tab === 'activities' && (
        <div className="space-y-2">
          <div className="flex justify-end">
            <button onClick={() => setShowActivityForm(true)} className="btn btn-primary flex items-center gap-1.5">
              <Plus className="w-4 h-4" /> Log Activity
            </button>
          </div>
          {!contact.activities?.length ? (
            <EmptyState>No activities yet. Use <b>Log Activity</b> to record one.</EmptyState>
          ) : contact.activities.map(a => <CrmActivityItem key={a.id} activity={a} onUpdated={() => queryClient.invalidateQueries({ queryKey: ['crm-contact', id] })} />)}
        </div>
      )}

      <ComposeEmailModal open={composing} onClose={() => setComposing(false)} defaults={{ to: contact.email ?? '', contactId: contact.id, organizationId: contact.organizationId }} />

      {editing && (
        <ContactForm
          contact={contact}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); queryClient.invalidateQueries({ queryKey: ['crm-contact', id] }); }}
        />
      )}

      {showActivityForm && (
        <CrmActivityForm
          defaults={{ contactId: id }}
          onClose={() => setShowActivityForm(false)}
          onSaved={() => { setShowActivityForm(false); queryClient.invalidateQueries({ queryKey: ['crm-contact', id] }); }}
        />
      )}
    </div>
  );
}
